import React, { useState, useEffect } from "react";
import { useParams, Link } from 'react-router-dom';

import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import ApiService from '../../services/ApiService';
import {formatDate, formatCurrency} from '../../utils/helper.js';

const UserDetails = () => {

  const { id } = useParams();

  const [isLoader, setIsLoader] = useState(true);
  const [bookingsLoader, setBookingsLoader] = useState(false);
  const [user, setUser] = useState({});
  const [activeTab, setActiveTab] = useState('info');
  const [bookings, setBookings] = useState([]);
  const [bookingsPagination, setBookingsPagination] = useState({});
  const [bookingsApiData, setBookingsApiData] = useState({
    search: "",
    status: "",
    payment_status: "",
    page: 1,
  });
  const [payments, setPayments] = useState([]);
  const [paymentsLoader, setPaymentsLoader] = useState(false);
  const [fuelRecords, setFuelRecords] = useState([]);
  const [fuelLoader, setFuelLoader] = useState(false);
  const [benefitsData, setBenefitsData] = useState({ membership_benefits: [], booking_benefits: [] });
  const [benefitsLoader, setBenefitsLoader] = useState(false);
  const [coursesData, setCoursesData] = useState({ orientations: [], club_briefings: [] });
  const [coursesLoader, setCoursesLoader] = useState(false);
  const [activeSubscriptionIndex, setActiveSubscriptionIndex] = useState(0);
  const [packages, setPackages] = useState([]);
  const [packagesLoader, setPackagesLoader] = useState(false);
  const [showPackageSelector, setShowPackageSelector] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('annual');
  const [paymentType, setPaymentType] = useState('cash');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showInstallmentsModal, setShowInstallmentsModal] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [installmentsLoader, setInstallmentsLoader] = useState(false);
  const [showFuelPayModal, setShowFuelPayModal] = useState(false);
  const [selectedFuelBooking, setSelectedFuelBooking] = useState(null);
  const [fuelPaymentMethod, setFuelPaymentMethod] = useState('cash');

  const documentsList = [
    { key: 'emirates_id_front', label: 'Emirates ID Front' },
    { key: 'emirates_id_back', label: 'Emirates ID Back' },
    { key: 'passport_front', label: 'Passport Front' },
    { key: 'passport_back', label: 'Passport Back' },
    { key: 'boat_license', label: 'Boat/Car License' },
    { key: 'eye_test', label: 'Eye Test' },
  ];

  useEffect(() => {
    if (id) {
      getSingleData();
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 'bookings') {
      getUserBookings();
    }
  }, [id, activeTab, bookingsApiData.page, bookingsApiData.search, bookingsApiData.status, bookingsApiData.payment_status]);

  useEffect(() => {
    if (id && activeTab === 'benefits') {
      getUserBenefits();
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      getActivePackages();
    }
  }, [activeTab]);

  const getUserCourses = async () => {
    try {
      setCoursesLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/users/${id}/courses`,
      });
      const data = response.data;
      if (data.status) {
        setCoursesData(data.data || { orientations: [], club_briefings: [] });
      } else {
        toast.error(data.message);
      }
      setCoursesLoader(false);
    } catch (error) {
      setCoursesLoader(false);
    }
  };

  useEffect(() => {
    if (id && activeTab === 'courses') {
      getUserCourses();
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (id && activeTab === 'fuel') {
      getUserFuel();
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (id && activeTab === 'payments') {
      getUserPayments();
    }
  }, [id, activeTab]);

  const getSingleData = async () => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/users/${id}`,
        data:{get_for:'details'}
      });
      const data = response.data;
      if (data.status) {
        const item = data.data.user;
        setUser(item);
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  };

  const getUserBookings = async () => {
    try {
      setBookingsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/users/${id}/bookings`,
        data: bookingsApiData
      });
      const data = response.data;
      if (data.status) {
        setBookings(data.data.bookings || []);
        setBookingsPagination(data.data.pagination || {});
      } else {
        toast.error(data.message);
      }
      setBookingsLoader(false);
    } catch (error) {
      setBookingsLoader(false);
    }
  };

  const getUserPayments = async () => {
    try {
      setPaymentsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/users/${id}/payments`,
      });
      const data = response.data;
      if (data.status) {
        setPayments(data.data.payments || []);
      } else {
        toast.error(data.message);
      }
      setPaymentsLoader(false);
    } catch (error) {
      setPaymentsLoader(false);
    }
  };

  const getUserFuel = async () => {
    try {
      setFuelLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/users/${id}/fuel`,
      });
      const data = response.data;
      if (data.status) {
        setFuelRecords(data.data.fuel_records || []);
      } else {
        toast.error(data.message);
      }
      setFuelLoader(false);
    } catch (error) {
      setFuelLoader(false);
    }
  };

  const handlePayFuelClick = (record) => {
    setSelectedFuelBooking(record);
    setFuelPaymentMethod('cash');
    setShowFuelPayModal(true);
  };

  const submitFuelPayment = async () => {
    if (!selectedFuelBooking) return;
    
    try {
      const response = await ApiService.request({
        method: 'POST',
        url: `admin/bookings/${selectedFuelBooking.id}/pay-fuel`,
        data: { payment_method: fuelPaymentMethod }
      });
      const data = response.data;
      if (data.status) {
        toast.success(data.message || 'Fuel payment processed successfully');
        setShowFuelPayModal(false);
        setSelectedFuelBooking(null);
        getUserFuel();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    }
  };

  const getUserBenefits = async () => {
    try {
      setBenefitsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/users/${id}/benefits`,
      });
      const data = response.data;
      if (data.status) {
        setBenefitsData(data.data || { membership_benefits: [], booking_benefits: [] });
      } else {
        toast.error(data.message);
      }
      setBenefitsLoader(false);
    } catch (error) {
      setBenefitsLoader(false);
    }
  };

  const handleBookingsPageChange = (event) => {
    setBookingsApiData(prev => ({ ...prev, page: event }));
  };

  const clearBookingsFilter = () => {
    setBookingsApiData({
      search: '',
      status: '',
      payment_status: '',
      page: 1
    });
  };

  const handleFreeze = async () => {
    const result = await Swal.fire({
      title: 'Are you sure to freeze this account?',
      text: "The user will not be able to login!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Freeze',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No',
    });

    if (result.isConfirmed) {
      try {
        setIsLoader(true);
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/users/${id}/freeze`,
        });
        const data = response.data;
        if (data.status) {
          toast.success(data.message);
          getSingleData();
        } else {
          toast.error(data.message);
        }
        setIsLoader(false);
      } catch (error) {
        setIsLoader(false);
        toast.error('Something went wrong');
      }
    }
  };

  const handleUnfreeze = async () => {
    const result = await Swal.fire({
      title: 'Are you sure to unfreeze this account?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Unfreeze',
      confirmButtonColor: '#4BC95F',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No',
    });

    if (result.isConfirmed) {
      try {
        setIsLoader(true);
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/users/${id}/unfreeze`,
        });
        const data = response.data;
        if (data.status) {
          toast.success(data.message);
          getSingleData();
        } else {
          toast.error(data.message);
        }
        setIsLoader(false);
      } catch (error) {
        setIsLoader(false);
        toast.error('Something went wrong');
      }
    }
  };

  const updateApprovalStatus = async (newStatus) => {
    const result = await Swal.fire({
      title: `Are you sure to ${newStatus}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#4BC95F',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No',
    });
    if (result.isConfirmed) {
      try {
        setIsLoader(true);
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/users/approval/${id}`,
          data: { approval_status: newStatus },
        });
        const data = response.data;
        if (data.status) {
          toast.success(data.message);
          getSingleData();
        } else {
          toast.error(data.message);
        }
        setIsLoader(false);
      } catch (error) {
        setIsLoader(false);
      }
    }
  };

  const handleExport = () => {
    const csvRows = [];
    const headers = ['Name', 'Email', 'Phone', 'Emirates ID', 'Passport', 'Status', 'Member Status', 'Created Date'];
    csvRows.push(headers.join(','));

    const values = [
      `"${user?.name || ''}"`,
      `"${user?.email || ''}"`,
      `"${user?.phone_code || ''} ${user?.phone || ''}"`,
      `"${user?.emirates_id || ''}"`,
      `"${user?.passport || ''}"`,
      `"${user?.status ? 'Active' : 'Inactive'}"`,
      `"${user?.member_status || ''}"`,
      `"${user?.created_at ? formatDate(user.created_at, 'DD MMM YYYY') : ''}"`,
    ];
    csvRows.push(values.join(','));

    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `member_details_${user?.name || 'user'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendNotification = async () => {
    const { value: text } = await Swal.fire({
      input: 'textarea',
      inputLabel: 'Message',
      inputPlaceholder: 'Type your notification message here...',
      inputAttributes: {
        'aria-label': 'Type your notification message here'
      },
      showCancelButton: true,
      title: 'Send App Notification',
      confirmButtonText: 'Send',
      showLoaderOnConfirm: true,
      preConfirm: async (text) => {
        if (!text) {
          Swal.showValidationMessage('Message is required');
          return false;
        }
        return new Promise(resolve => setTimeout(resolve, 1000)); 
      }
    });

    if (text) {
      Swal.fire('Sent!', 'Notification has been sent successfully.', 'success');
    }
  };

  const handleSendEmail = async () => {
    const { value: text } = await Swal.fire({
      input: 'textarea',
      inputLabel: 'Email Body',
      inputPlaceholder: 'Type your email content here...',
      showCancelButton: true,
      title: 'Send Email',
      confirmButtonText: 'Send',
      showLoaderOnConfirm: true,
      preConfirm: async (text) => {
        if (!text) {
          Swal.showValidationMessage('Content is required');
          return false;
        }
        return new Promise(resolve => setTimeout(resolve, 1000)); 
      }
    });

    if (text) {
      Swal.fire('Sent!', 'Email has been sent successfully.', 'success');
    }
  };

  const handleEditDates = async (subscription) => {
    const { value: formValues } = await Swal.fire({
      title: 'Edit Subscription Dates',
      html:
        '<div class="mb-3 text-start">' +
          '<label class="form-label required">Start Date</label>' +
          `<input id="swal-input-start" type="date" class="form-control border border-2" value="${subscription.start_date ? subscription.start_date.split('T')[0] : ''}">` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label required">End Date</label>' +
          `<input id="swal-input-end" type="date" class="form-control border border-2" value="${subscription.end_date ? subscription.end_date.split('T')[0] : ''}">` +
        '</div>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update Dates',
      preConfirm: () => {
        const start = document.getElementById('swal-input-start').value;
        const end = document.getElementById('swal-input-end').value;
        if (!start || !end) {
          Swal.showValidationMessage('Both dates are required');
        }
        return [start, end];
      }
    });

    if (formValues) {
      const [start_date, end_date] = formValues;
      try {
        setIsLoader(true);
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/subscriptions/${subscription.id}/dates`,
          data: { start_date, end_date }
        });
        const data = response.data;
        if (data.status) {
          toast.success(data.message || 'Dates updated successfully');
          getSingleData();
        } else {
          toast.error(data.message || 'Failed to update dates');
        }
        setIsLoader(false);
      } catch (error) {
        setIsLoader(false);
        toast.error('Something went wrong');
      }
    }
  };

  const handleSubscriptionAction = async (action, subscription) => {
    if (action === 'freeze') {
        const { value: formValues } = await Swal.fire({
          title: 'Freeze Subscription',
          html:
            '<p class="text-muted mb-4">Specify the freeze period. This will also update the user\'s freeze status.</p>' +
            '<div class="mb-3 text-start">' +
              '<label class="form-label required">From Date</label>' +
              `<input id="swal-input-from" type="date" class="form-control border border-2" value="${new Date().toISOString().split('T')[0]}">` +
            '</div>' +
            '<div class="mb-3 text-start">' +
              '<label class="form-label">To Date </label>' +
              '<input id="swal-input-to" type="date" class="form-control border border-2">' +
            '</div>',
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: 'Freeze',
          confirmButtonColor: '#f1416c',
          preConfirm: () => {
            const from = document.getElementById('swal-input-from').value;
            const to = document.getElementById('swal-input-to').value;
            if (!from) {
              Swal.showValidationMessage('From Date is required');
            }
            return [from, to];
          }
        });

        if (formValues) {
            const [freeze_start_date, freeze_end_date] = formValues;
            try {
                const response = await ApiService.request({
                  method: 'POST',
                  url: `admin/subscriptions/${subscription.id}/status`,
                  data: { 
                      status: 'frozen',
                      freeze_start_date,
                      freeze_end_date: freeze_end_date || null
                  }
                });
                const data = response.data;
                if (data.status) {
                  toast.success('Subscription frozen successfully');
                  getSingleData();
                } else {
                  toast.error(data.message || 'Failed to freeze subscription');
                }
            } catch (error) {
                toast.error('Something went wrong');
            }
        }
        return;
    }

    const result = await Swal.fire({
      title: `Confirm ${action}`,
      text: `Apply ${action} on ${subscription?.package?.name || 'Subscription'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel'
    });
    if (result.isConfirmed) {
      try {
        const statusMap = {
          cancel: 'cancelled',
          suspend: 'suspended',
          activate: 'active',
          deactivate: 'deactivated',
          freeze: 'frozen',
          renew: 'active'
        };
        const newStatus = statusMap[action];
        if (!newStatus) {
          toast.info(`${action} requested`);
          return;
        }
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/subscriptions/${subscription.id}/status`,
          data: { status: newStatus }
        });
        const data = response.data;
        if (data.status) {
          toast.success('Subscription status updated');
          getSingleData();
        } else {
          toast.error(data.message || 'Failed to update subscription');
        }
      } catch (error) {
        toast.error('Something went wrong');
      }
    }
  };

  const getActivePackages = async () => {
    try {
      setPackagesLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: 'admin/packages/active/list'
      });
      const data = response.data;
      if (data.status) {
        const payload = data.data;
        const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.packages) ? payload.packages : []);
        setPackages(list);
      }
      setPackagesLoader(false);
    } catch (error) {
      setPackagesLoader(false);
    }
  };

  const assignSubscriptionToUser = async () => {
    if (!selectedPackageId) {
      toast.error('Please select a package');
      return;
    }
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: 'POST',
        url: `admin/users/${id}/update`,
        data: {
          package_id: selectedPackageId,
          package_term: selectedTerm,
          payment_status: paymentType === 'complimentary' ? 'completed' : 'pending',
          payment_method: paymentType
        }
      });
      const data = response.data;
      if (data.status) {
        toast.success(data.message || 'Subscription assigned successfully');
        setShowPackageSelector(false);
        setSelectedPackageId('');
        setSelectedTerm('annual');
        getSingleData();
      } else {
        toast.error(data.message || 'Failed to assign subscription');
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
      toast.error('Something went wrong');
    }
  };


  const handleGenerateReceipt = async (payment) => {
    try {
        const response = await ApiService.request({
            method: 'GET',
            url: 'admin/users/payments/receipt',
            data: {
                type: payment.type,
                id: payment.id,
            },
            responseType: 'blob',
        });

        // Check if response is actually a PDF
        if (response.data.type && !response.data.type.includes('application/pdf')) {
             const text = await response.data.text();
             const json = JSON.parse(text);
             toast.error(json.message || 'Failed to generate receipt');
             return;
        }

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `receipt-${payment.type}-${payment.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading receipt:', error);
        // Error is already handled by ApiService for network/server errors, 
        // but if it's a blob error we might want to show something
    }
  };

  const handleManageInstallments = async (subscription) => {
    setSelectedSubscription(subscription);
    setShowInstallmentsModal(true);
    if (subscription.installments) {
        setInstallments(subscription.installments);
    } else {
        await fetchInstallments(subscription.id);
    }
  };

  const fetchInstallments = async (subscriptionId) => {
    try {
      setInstallmentsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/subscriptions/${subscriptionId}/installments`,
      });
      const data = response.data;
      if (data.status) {
        setInstallments(data.data.installments || []);
      } else {
        toast.error(data.message);
      }
      setInstallmentsLoader(false);
    } catch (error) {
      setInstallmentsLoader(false);
      toast.error('Failed to fetch installments');
    }
  };

  const handleAddInstallment = async () => {
    const planned = installments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
    const total = selectedSubscription?.amount || 0;
    const remaining = Math.max(0, total - planned);

    const { value: formValues } = await Swal.fire({
      title: 'Add Installment',
      html:
        '<div class="mb-3 text-start">' +
          '<label class="form-label required">Amount</label>' +
          `<input id="swal-input-amount" type="number" class="form-control" placeholder="0.00" value="${remaining > 0 ? remaining : ''}">` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label required">Due Date</label>' +
          `<input id="swal-input-date" type="date" class="form-control" value="${new Date().toISOString().split('T')[0]}">` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Payment Method</label>' +
          '<select id="swal-input-method" class="form-select">' +
            '<option value="cash">Cash</option>' +
            '<option value="cheque">Cheque</option>' +
            '<option value="online">Online</option>' +
          '</select>' +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Cheque Number</label>' +
          '<input id="swal-input-cheque" type="text" class="form-control" placeholder="Optional">' +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Notes</label>' +
          '<textarea id="swal-input-notes" class="form-control" rows="2"></textarea>' +
        '</div>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Add',
      preConfirm: () => {
        const amount = document.getElementById('swal-input-amount').value;
        const due_date = document.getElementById('swal-input-date').value;
        const payment_method = document.getElementById('swal-input-method').value;
        const cheque_number = document.getElementById('swal-input-cheque').value;
        const notes = document.getElementById('swal-input-notes').value;
        
        if (!amount || !due_date) {
          Swal.showValidationMessage('Amount and Due Date are required');
        }
        return { amount, due_date, payment_method, cheque_number, notes, status: 'pending' };
      }
    });

    if (formValues) {
      try {
        setInstallmentsLoader(true);
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/subscriptions/${selectedSubscription.id}/installments`,
          data: formValues
        });
        const data = response.data;
        if (data.status) {
          toast.success('Installment added successfully');
          fetchInstallments(selectedSubscription.id);
        } else {
          toast.error(data.message);
        }
        setInstallmentsLoader(false);
      } catch (error) {
        setInstallmentsLoader(false);
        toast.error('Something went wrong');
      }
    }
  };

  const handleEditInstallment = async (installment) => {
    const { value: formValues } = await Swal.fire({
      title: 'Edit Installment',
      html:
        '<div class="mb-3 text-start">' +
          '<label class="form-label required">Amount</label>' +
          `<input id="swal-input-amount" type="number" class="form-control" value="${installment.amount}">` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label required">Due Date</label>' +
          `<input id="swal-input-date" type="date" class="form-control" value="${installment.due_date ? installment.due_date.split('T')[0] : ''}">` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Status</label>' +
          `<select id="swal-input-status" class="form-select">` +
            `<option value="pending" ${installment.status === 'pending' ? 'selected' : ''}>Pending</option>` +
            `<option value="paid" ${installment.status === 'paid' ? 'selected' : ''}>Paid</option>` +
            `<option value="overdue" ${installment.status === 'overdue' ? 'selected' : ''}>Overdue</option>` +
            `<option value="cancelled" ${installment.status === 'cancelled' ? 'selected' : ''}>Cancelled</option>` +
          `</select>` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Payment Method</label>' +
          `<select id="swal-input-method" class="form-select">` +
            `<option value="cash" ${installment.payment_method === 'cash' ? 'selected' : ''}>Cash</option>` +
            `<option value="cheque" ${installment.payment_method === 'cheque' ? 'selected' : ''}>Cheque</option>` +
            `<option value="online" ${installment.payment_method === 'online' ? 'selected' : ''}>Online</option>` +
          `</select>` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Cheque Number</label>' +
          `<input id="swal-input-cheque" type="text" class="form-control" value="${installment.cheque_number || ''}">` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Paid Date</label>' +
          `<input id="swal-input-paid-date" type="date" class="form-control" value="${installment.paid_date ? installment.paid_date.split('T')[0] : ''}">` +
        '</div>' +
        '<div class="mb-3 text-start">' +
          '<label class="form-label">Notes</label>' +
          `<textarea id="swal-input-notes" class="form-control" rows="2">${installment.notes || ''}</textarea>` +
        '</div>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      preConfirm: () => {
        const amount = document.getElementById('swal-input-amount').value;
        const due_date = document.getElementById('swal-input-date').value;
        const status = document.getElementById('swal-input-status').value;
        const payment_method = document.getElementById('swal-input-method').value;
        const cheque_number = document.getElementById('swal-input-cheque').value;
        const paid_date = document.getElementById('swal-input-paid-date').value;
        const notes = document.getElementById('swal-input-notes').value;
        
        if (!amount || !due_date) {
          Swal.showValidationMessage('Amount and Due Date are required');
        }
        return { amount, due_date, status, payment_method, cheque_number, paid_date, notes };
      }
    });

    if (formValues) {
      try {
        setInstallmentsLoader(true);
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/installments/${installment.id}/update`,
          data: formValues
        });
        const data = response.data;
        if (data.status) {
          toast.success('Installment updated successfully');
          fetchInstallments(selectedSubscription.id);
        } else {
          toast.error(data.message);
        }
        setInstallmentsLoader(false);
      } catch (error) {
        setInstallmentsLoader(false);
        toast.error('Something went wrong');
      }
    }
  };

  const handleDeleteInstallment = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setInstallmentsLoader(true);
        const response = await ApiService.request({
          method: 'DELETE',
          url: `admin/installments/${id}`,
        });
        const data = response.data;
        if (data.status) {
          toast.success('Installment deleted successfully');
          fetchInstallments(selectedSubscription.id);
        } else {
          toast.error(data.message);
        }
        setInstallmentsLoader(false);
      } catch (error) {
        setInstallmentsLoader(false);
        toast.error('Something went wrong');
      }
    }
  };

  const handleEditBenefit = async (benefit) => {
    const { value: newValue } = await Swal.fire({
      title: `Edit ${benefit.label}`,
      input: 'number',
      inputLabel: 'Total Limit',
      inputValue: benefit.total,
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'You need to write something!'
        }
      }
    });

    if (newValue) {
      try {
        setIsLoader(true);
        const response = await ApiService.request({
          method: 'POST',
          url: `admin/users/${id}/benefits/update`,
          data: {
            key: benefit.key,
            value: newValue
          }
        });
        const data = response.data;
        if (data.status) {
          toast.success(data.message);
          getUserBenefits();
        } else {
          toast.error(data.message);
        }
        setIsLoader(false);
      } catch (error) {
        setIsLoader(false);
        toast.error('Something went wrong');
      }
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div
          id="kt_app_toolbar_container"
          className="app-container container-xxl d-flex flex-stack"
        >
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Member Details
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/dashboard" className="text-muted text-hover-primary">
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/users" className="text-muted text-hover-primary">
                  Users
                </Link>
              </li>
            </ul>
          </div>
            <div className="d-flex align-items-center gap-2">
              {!isLoader && user?.status && (
                <>
                  <button 
                    className="btn btn-lg btn-light-primary"
                    onClick={handleSendNotification}
                    title="Send App Notification"
                  >
                    <i className="fas fa-bell fs-2"></i>
                  </button>
                  <button 
                    className="btn btn-lg btn-light-info"
                    onClick={handleSendEmail}
                    title="Send Email"
                  >
                    <i className="fas fa-envelope fs-2"></i>
                  </button>
                  <button 
                    className="btn btn-lg btn-light-success"
                    onClick={handleExport}
                    title="Export to Excel"
                  >
                    <i className="fas fa-file-export fs-2"></i>
                  </button>
                </>
              )}
              {!isLoader && (
                <Link 
                  to={`/users/edit/${id}`} 
                  className="btn btn-lg btn-base"
                  title="Edit User"
                >
                  <i className="fas fa-edit fs-2"></i> Edit User
                </Link>
              )}
              {!isLoader && user?.approval_status === 'pending' && (
                <>
                  <button 
                    className="btn btn-lg btn-success"
                    onClick={() => updateApprovalStatus('approved')}
                    title="Approve User"
                  >
                    <i className="fas fa-check fs-2"></i> Approve
                  </button>
                  <button 
                    className="btn btn-lg btn-danger"
                    onClick={() => updateApprovalStatus('rejected')}
                    title="Reject User"
                  >
                    <i className="fas fa-times fs-2"></i> Reject
                  </button>
                </>
              )}
            </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-header border-0 pt-6">
              <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold">
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'info' ? 'active' : ''}`}
                    onClick={() => setActiveTab('info')}
                  >
                    <i className="fas fa-info-circle me-2"></i> Info
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'subscriptions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subscriptions')}
                  >
                    <i className="fas fa-file-invoice-dollar me-2"></i> Subscriptions
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'bookings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bookings')}
                  >
                    <i className="fas fa-ship me-2"></i> Boat Bookings
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'fuel' ? 'active' : ''}`}
                    onClick={() => setActiveTab('fuel')}
                  >
                    <i className="fas fa-gas-pump me-2"></i> Fuel
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('payments')}
                  >
                    <i className="fas fa-money-bill-wave me-2"></i> Payments
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'documents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('documents')}
                  >
                    <i className="fas fa-file-alt me-2"></i> Documents
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'penalties' ? 'active' : ''}`}
                    onClick={() => setActiveTab('penalties')}
                  >
                    <i className="fas fa-exclamation-triangle me-2"></i> Penalties
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'referrals' ? 'active' : ''}`}
                    onClick={() => setActiveTab('referrals')}
                  >
                    <i className="fas fa-user-friends me-2"></i> Referrals
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'rewards' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rewards')}
                  >
                    <i className="fas fa-gift me-2"></i> Rewards
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'benefits' ? 'active' : ''}`}
                    onClick={() => setActiveTab('benefits')}
                  >
                    <i className="fas fa-star me-2"></i> Benefits
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'courses' ? 'active' : ''}`}
                    onClick={() => setActiveTab('courses')}
                  >
                    <i className="fas fa-graduation-cap me-2"></i> Courses
                  </button>
                </li>
                <li className="nav-item mt-2">
                  <button
                    className={`nav-link text-active-primary ms-0 me-10 ${activeTab === 'captains' ? 'active' : ''}`}
                    onClick={() => setActiveTab('captains')}
                  >
                    <i className="fas fa-user-tie me-2"></i> Captains
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body pt-5">
              {isLoader && (
                <Loader color="black" className="mt-5" text="Loading" size="30" />
              )}

              {!isLoader && activeTab === 'info' && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="row g-5 mt-5">
                        <div className="col-lg-6">
                            <div className="card shadow-sm h-100 border">
                                <div className="card-header">
                                    <h3 className="card-title fw-bold fs-4">Personal Details</h3>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex flex-center flex-column mb-5">
                                        <div className="symbol symbol-100px mb-3">
                                            <img src={user.picture || '/placeholder.png'} alt="user" className="w-100px h-100px object-cover rounded-circle border" />
                                        </div>
                                        <span className="fs-4 text-gray-800 text-hover-primary fw-bold mb-1">{user?.name || 'N/A'}</span>
                                        <div className="fs-6 fw-semibold text-muted mb-6">{user?.member_status || 'N/A'} Member</div>
                                    </div>
                                    
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Emirates ID</div>
                                        <div className="text-gray-600">{user?.emirates_id || 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Passport</div>
                                        <div className="text-gray-600">{user?.passport || 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Nationality</div>
                                        <div className="text-gray-600">{user?.nationality || 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Member ID</div>
                                        <div className="text-gray-600">{user?.member_id || 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Membership ID</div>
                                        <div className="text-gray-600">{user?.membership_id || 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Registered Date</div>
                                        <div className="text-gray-600">{user?.created_at ? formatDate(user.created_at, 'DD MMM YYYY') : 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-lg-6">
                            <div className="card shadow-sm h-100 border">
                                <div className="card-header">
                                    <h3 className="card-title fw-bold fs-4">Contact & Status</h3>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Email</div>
                                        <div className="text-gray-600">{user?.email || 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Phone</div>
                                        <div className="text-gray-600">{user?.phone ? `${user.phone_code || ''} ${user.phone}` : 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Phone Verified</div>
                                        <div className="text-gray-600">{user?.phone_verified_at ? 'Yes' : 'No'}</div>
                                    </div>

                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Membership Plan</div>
                                        <div className="text-gray-800 fw-semibold">{user.subscriptions && user.subscriptions[activeSubscriptionIndex]?.package?.name || 'N/A'}</div>
                                    </div>
                                    <div className="separator separator-dashed my-3"></div>
                                    
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Account Status</div>
                                        <div>
                                            <span className={`badge text-capitalize ${user?.status==='active' ? 'badge-light-success' : 'badge-light-danger'} me-2 fs-7`}>
                                                {user?.status || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {user?.status === 'frozen' && (
                                        <>
                                            <div className="separator separator-dashed my-3"></div>
                                            <div className="d-flex flex-stack fs-6 py-3">
                                                <div className="fw-semibold rotate collapsible">Frozen Period</div>
                                                <div className="text-gray-600">
                                                    {user?.frozen_from ? formatDate(user.frozen_from, 'DD MMM YYYY') : 'N/A'} 
                                                    {user?.frozen_to ? ` - ${formatDate(user.frozen_to, 'DD MMM YYYY')}` : ''}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    
                                    <div className="separator separator-dashed my-3"></div>
                                    <div className="d-flex flex-stack fs-6 py-3">
                                        <div className="fw-semibold rotate collapsible">Approval Status</div>
                                        <div>
                                            <span className={`badge text-capitalize badge-light-${user?.approval_status === 'approved' ? 'success' : user?.approval_status === 'rejected' ? 'danger' : 'warning'} fs-7`}>
                                                {user?.approval_status ? user.approval_status : 'pending'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>
              )}

              {!isLoader && activeTab === 'fuel' && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                     <div className="card shadow-sm mt-5">
                        <div className="card-header">
                            <h3 className="card-title fw-bold fs-4">Fuel History</h3>
                        </div>
                        <div className="card-body">
                            {fuelLoader ? (
                                <Loader color="black" className="mt-5" text="Loading" size="30" />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                                        <thead>
                                            <tr className="fw-bold text-muted">
                                                <th>Date</th>
                                                <th>Booking Ref</th>
                                                <th>Boat</th>
                                                <th>Location</th>
                                                <th>Fuel Consumed</th>
                                                <th>Price</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fuelRecords.length > 0 ? (
                                                fuelRecords.map((record, index) => (
                                                    <tr key={index}>
                                                        <td>{formatDate(record.date, 'DD MMM YYYY')}</td>
                                                        <td>{record.booking_reference}</td>
                                                        <td>{record.boat_name}</td>
                                                        <td>{record.location}</td>
                                                        <td>{record.fuel_consumed} L</td>
                                                        <td>{formatCurrency(record.fuel_price || '0.00')}</td>
                                                        <td>
                                                            <span className={`badge text-capitalize badge-light-${record.fuel_payment_status === 'paid' ? 'success' : 'warning'}`}>
                                                                {record.fuel_payment_status || 'pending'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {record.fuel_payment_status !== 'paid' && (
                                                              <div>
                                                                <button 
                                                                    className="btn btn-sm btn-light-primary"
                                                                    onClick={() => handlePayFuelClick(record)}
                                                                >
                                                                    Pay Now
                                                                </button>
                                                              </div>
                                                            )}
                                                            {record.fuel_payment_status === 'paid' && (
                                                              <div>
                                                                <span className="text-muted fs-7">
                                                                    Paid via {record.fuel_payment_method}
                                                                </span> <br />

                                                                <button 
                                                                    className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2"
                                                                    // onClick={() => handleGenerateReceipt(payment)}
                                                                    title="Download Receipt"
                                                                >
                                                                    <i className="fas fa-file-invoice fs-5"></i>
                                                                </button>
                                                                <button 
                                                                    className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2"
                                                                    // onClick={() => handleGenerateReceipt(payment)}
                                                                    title="Download Invoice"
                                                                >
                                                                    <i className="fas fa-file-invoice fs-5"></i>
                                                                </button>
                                                              </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted">No fuel records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                     </div>
                  </div>
                </div>
              )}

              {!isLoader && activeTab === 'subscriptions' && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    {user.subscriptions && user.subscriptions.length > 0 ? (
                      <>
                        <div className="d-flex align-items-center justify-content-between mt-5">
                          <div className="fs-6 fw-semibold">Subscription {activeSubscriptionIndex + 1} of {user.subscriptions.length}</div>
                          <div className="btn-group">
                            <button className="btn btn-sm btn-light-primary" onClick={() => setActiveTab('payments')} > Manage Installment</button>
                            <button
                              className="btn btn-sm btn-light"
                              disabled={activeSubscriptionIndex === 0}
                              onClick={() => setActiveSubscriptionIndex(prev => Math.max(prev - 1, 0))}
                            >
                              <i className="fas fa-chevron-left"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light"
                              disabled={activeSubscriptionIndex === (user.subscriptions.length - 1)}
                              onClick={() => setActiveSubscriptionIndex(prev => Math.min(prev + 1, user.subscriptions.length - 1))}
                            >
                              <i className="fas fa-chevron-right"></i>
                            </button>
                          </div>
                        </div>

                        <div className="card shadow-sm mt-4" style={{ background: `linear-gradient(to bottom, ${(user.subscriptions[activeSubscriptionIndex]?.package?.bg_color || '').split(',')[0]?.trim() || '#f8f9fa'}, ${(user.subscriptions[activeSubscriptionIndex]?.package?.bg_color || '').split(',')[1]?.trim() || '#f8f9fa'})` }}>
                          <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center w-100">
                              <div>
                                <h3 className="card-title fw-bold fs-4">{user.subscriptions[activeSubscriptionIndex]?.package?.name || 'N/A'}</h3>
                                <div className="text-dark fs-7">Term: {user.subscriptions[activeSubscriptionIndex]?.term || 'N/A'}</div>
                              </div>
                              <div className="text-end">
                                <div className="fs-5 fw-bold">{formatCurrency(user.subscriptions[activeSubscriptionIndex]?.total_amount || '0')}</div>
                                <div className="mt-2">
                                  <span className={`badge text-capitalize ${user.subscriptions[activeSubscriptionIndex]?.status === 'active' ? 'badge-light-success' : 'badge-light-danger'} me-2`}>
                                    {user.subscriptions[activeSubscriptionIndex]?.status || 'N/A'}
                                  </span>
                                  <span className={`badge text-capitalize ${user.subscriptions[activeSubscriptionIndex]?.payment_status === 'completed' ? 'badge-light-success' : user.subscriptions[activeSubscriptionIndex]?.payment_status === 'pending' ? 'badge-light-warning' : 'badge-light-danger'}`}>
                                    {user.subscriptions[activeSubscriptionIndex]?.payment_status || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="row g-5">
                              <div className="col-md-6">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="fw-bold">Subscription Period</div>
                                    <button className="btn btn-sm btn-light-primary py-1 px-3" onClick={() => handleEditDates(user.subscriptions[activeSubscriptionIndex])}>
                                      <i className="fas fa-edit me-1"></i> Edit Dates
                                    </button>
                                </div>
                                <div className="d-flex flex-stack fs-6 py-3">
                                  <div className="fw-semibold">Start Date</div>
                                  <div className="text-gray-800">{user.subscriptions[activeSubscriptionIndex]?.start_date ? formatDate(user.subscriptions[activeSubscriptionIndex].start_date, 'DD MMM YYYY') : 'N/A'}</div>
                                </div>
                                <div className="separator separator-dashed my-3"></div>
                                <div className="d-flex flex-stack fs-6 py-3">
                                  <div className="fw-semibold">End Date</div>
                                  <div className="text-gray-800">{user.subscriptions[activeSubscriptionIndex]?.end_date ? formatDate(user.subscriptions[activeSubscriptionIndex].end_date, 'DD MMM YYYY') : 'N/A'}</div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                {user.subscriptions[activeSubscriptionIndex]?.status === 'active' && (
                                  <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                                    <button className="btn btn-light-danger" onClick={() => handleSubscriptionAction('cancel', user.subscriptions[activeSubscriptionIndex])}>Cancel</button>
                                    <button className="btn btn-light-primary" onClick={() => { setShowPackageSelector(true); }}>Upgrade/Downgrade</button>
                                    <button className="btn btn-light-warning" onClick={() => handleSubscriptionAction('freeze', user.subscriptions[activeSubscriptionIndex])}>Freeze Subscription</button>
                                  </div>
                                )}
                                {user.subscriptions[activeSubscriptionIndex]?.status === 'frozen' && (
                                  <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                                    <button className="btn btn-light-success" onClick={() => handleSubscriptionAction('activate', user.subscriptions[activeSubscriptionIndex])}>Unfreeze Subscription</button>
                                  </div>
                                )}
                                {['cancelled', 'expired'].includes(user.subscriptions[activeSubscriptionIndex]?.status) && (
                                  <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                                    <button className="btn btn-light-success" onClick={() => { setShowPackageSelector(true); }}>Renew Subscription</button>
                                  </div>
                                )}
                              </div>
                            </div>
                            {showPackageSelector && (
                              <div className="mt-5">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h4 className="fw-bold">Select Package</h4>
                                  <button className="btn btn-sm btn-light" onClick={() => { setShowPackageSelector(false); setSelectedPackageId(''); setSelectedTerm('annual'); }}>Close</button>
                                </div>
                                {packagesLoader ? (
                                  <Loader text="Loading" size="30" className="text-center" />
                                ) : (
                                  <>
                                    <div className="row g-4">
                                      {Array.isArray(packages) && packages.length > 0 ? packages.map((pkg) => (
                                        <div className="col-md-4" key={pkg.id}>
                                          <div className={`card h-100 bg-gradient-right text-white border ${selectedPackageId === pkg.id ? 'border-primary' : ''}`} onClick={() => setSelectedPackageId(pkg.id)} style={{ cursor: 'pointer' }}>
                                            <div className="card-header ">
                                              <h5 className="card-title text-white fw-bold mb-0">{pkg.name}</h5>
                                            </div>
                                            <div className="card-body">
                                              <div className="d-flex flex-column gap-2">
                                                <div className="d-flex justify-content-between">
                                                  <span className="fw-semibold">Monthly</span>
                                                  <span>{formatCurrency(pkg.monthly_price || 0)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                  <span className="fw-semibold">Semi-Annual</span>
                                                  <span>{formatCurrency(pkg.semi_annual_price || 0)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                  <span className="fw-semibold">Annual</span>
                                                  <span>{formatCurrency(pkg.annual_price || 0)}</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )) : (
                                        <div className="text-muted">No packages available</div>
                                      )}
                                    </div>
                                    <div className="row">
                                      <div className="mt-4 col-md-6">
                                        <div className="fw-semibold mb-2">Select Term</div>
                                        <div className="d-flex flex-wrap gap-3">
                                          <div className="form-check">
                                            <input className="form-check-input" type="radio" name="term" id="termMonthly" checked={selectedTerm === 'monthly'} onChange={() => setSelectedTerm('monthly')} />
                                            <label className="form-check-label" htmlFor="termMonthly">Monthly</label>
                                          </div>
                                          <div className="form-check">
                                            <input className="form-check-input" type="radio" name="term" id="termSemiAnnual" checked={selectedTerm === 'semi_annual'} onChange={() => setSelectedTerm('semi_annual')} />
                                            <label className="form-check-label" htmlFor="termSemiAnnual">Semi-Annual</label>
                                          </div>
                                          <div className="form-check">
                                            <input className="form-check-input" type="radio" name="term" id="termAnnual" checked={selectedTerm === 'annual'} onChange={() => setSelectedTerm('annual')} />
                                            <label className="form-check-label" htmlFor="termAnnual">Annual</label>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="mt-4 col-md-6">
                                        <div className="fw-semibold mb-2">Payment Type</div>
                                        <div className="d-flex flex-wrap gap-3">
                                          {['cash', 'cheques', 'online', 'complimentary'].map((type) => (
                                            <div className="form-check" key={type}>
                                              <input
                                                className="form-check-input"
                                                type="radio"
                                                name="paymentType"
                                                id={`paymentType_${type}`}
                                                checked={paymentType === type}
                                                onChange={() => setPaymentType(type)}
                                              />
                                              <label className="form-check-label text-capitalize" htmlFor={`paymentType_${type}`}>
                                                {type}
                                              </label>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="d-flex justify-content-end mt-4">
                                      <button className="btn btn-base" onClick={assignSubscriptionToUser} disabled={!selectedPackageId}>Confirm Change</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="mt-5">
                        <div className="card shadow-sm">
                          <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title fw-bold">Assign Membership</h3>
                          </div>
                          <div className="card-body">
                            {packagesLoader ? (
                              <Loader text="Loading" size="30" className="text-center" />
                            ) : (
                              <>
                                <div className="row g-4">
                                  {Array.isArray(packages) && packages.length > 0 ? packages.map((pkg) => (
                                    <div className="col-md-4" key={pkg.id}>
                                      <div className={`card h-100 border ${selectedPackageId === pkg.id ? 'border-primary' : ''}`} onClick={() => setSelectedPackageId(pkg.id)} 
                                        style={{ cursor: 'pointer', background: `linear-gradient(to bottom, ${(pkg.bg_color || '').split(',')[0]?.trim() || '#f8f9fa'}, ${(pkg.bg_color || '').split(',')[1]?.trim() || '#f8f9fa'})` }}>
                                        <div className="card-header">
                                          <h5 className="card-title fw-bold mb-0">{pkg.name}</h5>
                                        </div>
                                        <div className="card-body">
                                          <div className="d-flex flex-column gap-2">
                                            <div className="d-flex justify-content-between">
                                              <span className="fw-semibold">Monthly</span>
                                              <span>{formatCurrency(pkg.monthly_price || 0)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                              <span className="fw-semibold">Semi-Annual</span>
                                              <span>{formatCurrency(pkg.semi_annual_price || 0)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                              <span className="fw-semibold">Annual</span>
                                              <span>{formatCurrency(pkg.annual_price || 0)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )) : (
                                    <div className="text-muted">No packages available</div>
                                  )}
                                </div>
                                <div className="row">
                                  <div className="mt-4 col-md-6">
                                    <div className="fw-semibold my-3">Select Term</div>
                                    <div className="d-flex flex-wrap gap-3">
                                      <div className="form-check">
                                        <input className="form-check-input" type="radio" name="term2" id="termMonthly2" checked={selectedTerm === 'monthly'} onChange={() => setSelectedTerm('monthly')} />
                                        <label className="form-check-label" htmlFor="termMonthly2">Monthly</label>
                                      </div>
                                      <div className="form-check">
                                        <input className="form-check-input" type="radio" name="term2" id="termSemiAnnual2" checked={selectedTerm === 'semi_annual'} onChange={() => setSelectedTerm('semi_annual')} />
                                        <label className="form-check-label" htmlFor="termSemiAnnual2">Semi-Annual</label>
                                      </div>
                                      <div className="form-check">
                                        <input className="form-check-input" type="radio" name="term2" id="termAnnual2" checked={selectedTerm === 'annual'} onChange={() => setSelectedTerm('annual')} />
                                        <label className="form-check-label" htmlFor="termAnnual2">Annual</label>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mt-4 col-md-6">
                                    <div className="fw-semibold my-3">Payment Type</div>
                                    <div className="d-flex flex-wrap gap-3">
                                      {['cash', 'cheques', 'online', 'complimentary'].map((type) => (
                                        <div className="form-check" key={type}>
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name="paymentType2"
                                            id={`paymentType2_${type}`}
                                            checked={paymentType === type}
                                            onChange={() => setPaymentType(type)}
                                          />
                                          <label className="form-check-label text-capitalize" htmlFor={`paymentType2_${type}`}>
                                            {type}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex justify-content-end mt-4">
                                  <button className="btn btn-base" onClick={assignSubscriptionToUser} disabled={!selectedPackageId}>Assign Subscription</button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}


              {!isLoader && activeTab === 'bookings' && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="card-header border-0 pt-6">
                      <div className="w-100">
                        <form method="get">
                          <div className="row">
                            <div className="col-md-3">
                              <div className="d-flex align-items-center position-relative my-1">
                                <input 
                                  type="text" 
                                  value={bookingsApiData.search} 
                                  onChange={e => setBookingsApiData({ ...bookingsApiData, search: e.target.value, page: 1 })} 
                                  className="form-control form-control-solid me-5"
                                  placeholder="Search by reference or boat name.."
                                />
                              </div>
                            </div>
                            <div className="col-md-2">
                              <select
                                className="form-select form-control-solid"
                                value={bookingsApiData.status}
                                onChange={(e) => setBookingsApiData({ ...bookingsApiData, status: e.target.value, page: 1 })}
                              >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </div>
                            <div className="col-md-2">
                              <select
                                className="form-select form-control-solid"
                                value={bookingsApiData.payment_status}
                                onChange={(e) => setBookingsApiData({ ...bookingsApiData, payment_status: e.target.value, page: 1 })}
                              >
                                <option value="">Payment Status</option>
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                              </select>
                            </div>
                            <div className="col-md-2 mt-2">
                              <button 
                                type="button" 
                                onClick={clearBookingsFilter} 
                                className={`btn btn-sm btn-secondary ${!bookingsApiData.search && !bookingsApiData.status && !bookingsApiData.payment_status ? 'd-none' : ''}`}
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                    <div className="card-body pt-0">
                      <div className="table-responsive">
                        <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                          <thead>
                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                              <th>Booking Reference</th>
                              <th>Boat</th>
                              <th>Date & Time</th>
                              <th>Passengers</th>
                              <th>Location</th>
                              <th>Total Amount</th>
                              <th>Status</th>
                              <th>Payment Status</th>
                              <th>Created Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody className="fw-semibold text-gray-600 fs-6">
                            {bookings.length > 0 ? bookings.map((booking) => (
                              <tr key={booking.id}>
                                <td>
                                  <span className="fw-bold">#{booking.booking_reference}</span>
                                </td>
                                <td>
                                  {booking.boat && 
                                    <div>
                                      {booking.boat.main_image && (
                                        <img 
                                          src={booking.boat.main_image} 
                                          alt="boat" 
                                          className="w-50px h-50px object-cover rounded me-2" 
                                        />
                                      )}
                                      <span>{booking.boat.name}</span>
                                    </div>
                                  }
                                </td>
                                <td>
                                  <div>
                                    <small className="fw-bold">{formatDate(booking.booking_date, 'DD MMM YYYY')}</small>
                                    <br />
                                    <small className="text-muted">{booking.booking_time}</small>
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    <small>{booking.adults} Adults</small>
                                    {booking.children > 0 && (
                                      <>
                                        <br />
                                        <small className="text-muted">{booking.children} Children</small>
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-truncate" style={{maxWidth: '150px'}} title={booking.location?.name || booking.location}>
                                    {booking.location?.name || booking.location || 'N/A'}
                                  </div>
                                </td>
                                <td>
                                  <span className="fw-bold">{formatCurrency(booking.total_amount)}</span>
                                  {booking.vat_amount > 0 && (
                                    <div>
                                      <small className="text-muted">VAT: {formatCurrency(booking.vat_amount)}</small>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <span className={`badge text-capitalize ${
                                    booking.status === 'completed' ? 'bg-success' :
                                    booking.status === 'confirmed' ? 'bg-primary' :
                                    booking.status === 'cancelled' ? 'bg-danger' :
                                    'bg-warning'
                                  }`}>
                                    {booking.status}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge text-capitalize ${
                                    booking.payment_status === 'completed' ? 'bg-success' :
                                    booking.payment_status === 'failed' ? 'bg-danger' :
                                    booking.payment_status === 'refunded' ? 'bg-info' :
                                    'bg-warning'
                                  }`}>
                                    {booking.payment_status}
                                  </span>
                                </td>
                                <td>{formatDate(booking.created_at, 'DD MMM YYYY, hh:mm A')}</td>
                                <td>
                                  <Link 
                                    to={`/bookings/${booking.id}`} 
                                    className="btn btn-sm h-30px btn-light-info me-1"
                                    title="View Details"
                                  >
                                    <i className="fas fa-eye fs-6"></i>
                                  </Link>
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan="10" className="text-dark text-center mt-2">
                                  {bookingsLoader ? 'Loading...' : 'No bookings found!'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {bookingsLoader && (
                          <Loader text="Loading" size="30" className="text-center" />
                        )}
                        {bookingsPagination && bookingsPagination.last_page > 1 && (
                          <Paginator
                            page={bookingsPagination.current_page || 1}
                            rows={bookingsPagination.per_page || 15}
                            totalRecords={bookingsPagination.total || 0}
                            onPageChange={handleBookingsPageChange}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!isLoader && activeTab === 'payments' && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="card-body py-3">
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="fw-bold text-muted">
                              <th className="min-w-150px">Date</th>
                              <th className="min-w-200px">Description</th>
                              <th className="min-w-100px">Amount</th>
                              <th className="min-w-100px">Method</th>
                              <th className="min-w-100px">Status</th>
                              <th className="min-w-100px">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.length > 0 ? (
                              payments.map((payment, index) => (
                                <tr key={index}>
                                  <td>{formatDate(payment.date, 'DD MMM YYYY')}</td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex justify-content-start flex-column">
                                            <span className="text-dark fw-bold text-hover-primary fs-6">{payment.description}</span>
                                            <span className="text-muted fw-semibold text-muted d-block fs-7">{payment.type}</span>
                                        </div>
                                    </div>
                                  </td>
                                  <td>{formatCurrency(payment.amount)}</td>
                                  <td className="text-capitalize">{payment.payment_method || '-'}</td>
                                  <td>
                                    <span className={`badge ${
                                      payment.status === 'completed' || payment.status === 'paid' || payment.status === 'active' ? 'badge-light-success' :
                                      payment.status === 'failed' || payment.status === 'overdue' ? 'badge-light-danger' :
                                      'badge-light-warning'
                                    }`}>
                                      {payment.status}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex justify-content-end align-items-center">
                                      <button 
                                          className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2"
                                          // onClick={() => handleGenerateReceipt(payment)}
                                          title="Download Receipt"
                                      >
                                          <i className="fas fa-file-invoice fs-5"></i>
                                      </button>
                                      <button 
                                          className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2"
                                          // onClick={() => handleGenerateReceipt(payment)}
                                          title="Download Invoice"
                                      >
                                          <i className="fas fa-file-invoice fs-5"></i>
                                      </button>
                                      {payment.type === 'Subscription' && (
                                        <>
                                          {payment.payment_method === 'complimentary' ? (
                                              <span className="badge badge-light-success">Fully Paid</span>
                                          ) : (
                                              <button 
                                                  className="btn btn-sm btn-light-primary"
                                                  onClick={() => handleManageInstallments(payment)}
                                              >
                                                  Manage Installments
                                              </button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center text-muted">No payments found</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Installments Modal */}
              {showInstallmentsModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Manage Installments</h5>
                        <button type="button" className="btn-close" onClick={() => setShowInstallmentsModal(false)}></button>
                      </div>
                      <div className="modal-body">
                        <div className="d-flex flex-column mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                             <div>
                                <h5 className="fw-bold text-gray-800">Installment Plan</h5>
                                <div className="text-muted fs-7">Manage payment schedule for this subscription</div>
                             </div>
                             <button className="btn btn-sm btn-base" onClick={handleAddInstallment}>
                                <i className="fas fa-plus me-1"></i> Add Installment
                             </button>
                          </div>
                          
                          <div className="row g-3">
                            <div className="col-md-4">
                                <div className="border border-gray-300 border-dashed rounded py-3 px-4">
                                    <div className="d-flex align-items-center">
                                        <div className="fs-6 fw-bold text-gray-700">Total Amount</div>
                                    </div>
                                    <div className="fs-2 fw-bold text-dark">{formatCurrency(selectedSubscription?.amount || '0.00')}</div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border border-gray-300 border-dashed rounded py-3 px-4">
                                    <div className="d-flex align-items-center">
                                        <div className="fs-6 fw-bold text-gray-700">Planned</div>
                                    </div>
                                    <div className="fs-2 fw-bold text-primary">
                                        {formatCurrency(installments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0).toFixed(2))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border border-gray-300 border-dashed rounded py-3 px-4">
                                    <div className="d-flex align-items-center">
                                        <div className="fs-6 fw-bold text-gray-700">Remaining</div>
                                    </div>
                                    <div className={`fs-2 fw-bold ${(selectedSubscription?.amount - installments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)) > 0 ? 'text-danger' : 'text-success'}`}>
                                        {formatCurrency(Math.max(0, (selectedSubscription?.amount || 0) - installments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)).toFixed(2))}
                                    </div>
                                </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="table-responsive">
                          <table className="table table-row-bordered align-middle gy-4 gs-9">
                            <thead className="border-bottom border-gray-200 fs-6 text-gray-600 fw-bold bg-light bg-opacity-75">
                              <tr>
                                <td>Due Date</td>
                                <td>Amount</td>
                                <td>Method</td>
                                <td>Cheque No.</td>
                                <td>Status</td>
                                <td>Actions</td>
                              </tr>
                            </thead>
                            <tbody>
                              {installmentsLoader ? (
                                <tr><td colSpan="6" className="text-center">Loading...</td></tr>
                              ) : installments.length > 0 ? (
                                installments.map((inst) => (
                                  <tr key={inst.id}>
                                    <td>{formatDate(inst.due_date, 'DD MMM YYYY')}</td>
                                    <td>{formatCurrency(inst.amount)}</td>
                                    <td className="text-capitalize">{inst.payment_method || '-'}</td>
                                    <td>{inst.cheque_number || '-'}</td>
                                    <td>
                                      <span className={`badge ${
                                        inst.status === 'paid' ? 'badge-light-success' :
                                        inst.status === 'overdue' ? 'badge-light-danger' :
                                        'badge-light-warning'
                                      }`}>
                                        {inst.status}
                                      </span>
                                    </td>
                                    <td>
                                      <button 
                                        className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                        onClick={() => handleEditInstallment(inst)}
                                      >
                                        <i className="fas fa-edit fs-5"></i>
                                      </button>
                                      <button 
                                        className="btn btn-icon btn-bg-light btn-active-color-danger btn-sm"
                                        onClick={() => handleDeleteInstallment(inst.id)}
                                      >
                                        <i className="fas fa-trash fs-5"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr><td colSpan="6" className="text-center">No installments found</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setShowInstallmentsModal(false)}>Close</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!isLoader && activeTab === 'benefits' && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="card-body py-3">
                      <div className="table-responsive">
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                          <thead>
                            <tr className="fw-bold text-muted">
                              <th className="min-w-200px">Benefit</th>
                              <th className="min-w-100px">Total</th>
                              <th className="min-w-100px">Used</th>
                              <th className="min-w-100px">Remaining</th>
                              <th className="min-w-100px">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {benefitsData.membership_benefits && benefitsData.membership_benefits.length > 0 ? (
                              benefitsData.membership_benefits.map((benefit, index) => (
                                <tr key={index}>
                                  <td>
                                    <span className="text-dark fw-bold text-hover-primary fs-6">{benefit.label}</span>
                                  </td>
                                  <td>
                                    <span className="badge badge-light-primary fs-7 fw-bold">{benefit.total} {benefit.unit}</span>
                                  </td>
                                  <td>
                                    <span className="badge badge-light-warning fs-7 fw-bold">{benefit.used} {benefit.unit}</span>
                                  </td>
                                  <td>
                                    <span className="badge badge-light-success fs-7 fw-bold">{benefit.remaining} {benefit.unit}</span>
                                  </td>
                                  <td>
                                    <button 
                                      className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                      onClick={() => handleEditBenefit(benefit)}
                                    >
                                      <i className="fas fa-edit fs-5"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center text-muted">No benefits found</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!isLoader && ['penalties', 'referrals', 'rewards', 'courses', 'captains'].includes(activeTab) && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                     <div className="d-flex flex-column flex-center flex-column-fluid p-10">
                        <h1 className="fw-bold text-gray-900 mb-7">Coming Soon</h1>
                        <div className="fw-semibold text-gray-500 mb-7">This feature is currently under development.</div>
                     </div>
                  </div>
                </div>
              )}

              {!isLoader && activeTab === 'documents' && (
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div className="row g-5 mt-5">
                      {documentsList.map((doc, index) => (
                        <div className="col-md-6" key={index}>
                          <div className="card shadow-sm h-100">
                            <div className="card-header">
                              <h3 className="card-title">{doc.label}</h3>
                            </div>
                            <div className="card-body text-center">
                              {user.document && user.document[doc.key] ? (
                                <a href={user.document[doc.key]} target="_blank" rel="noopener noreferrer">
                                  <img 
                                    src={user.document[doc.key]} 
                                    alt={doc.label} 
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '300px' }} 
                                  />
                                </a>
                              ) : (
                                <div className="p-5 text-muted bg-light rounded">
                                  No {doc.label} Uploaded
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Fuel Payment Modal */}
              {showFuelPayModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Pay Fuel Receipt</h5>
                        <button type="button" className="btn-close" onClick={() => setShowFuelPayModal(false)}></button>
                      </div>
                      <div className="modal-body">
                        <p><strong>Booking Ref:</strong> {selectedFuelBooking?.booking_reference}</p>
                        <p><strong>Fuel Consumed:</strong> {selectedFuelBooking?.fuel_consumed} Liters</p>
                        <p><strong>Amount to Pay:</strong> {formatCurrency(selectedFuelBooking?.fuel_price)}</p>
                        
                        <div className="mb-3">
                            <label className="form-label">Payment Method</label>
                            <div className="d-flex gap-4">
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="fuelPaymentMethod" 
                                        id="fuelCash" 
                                        value="cash"
                                        checked={fuelPaymentMethod === 'cash'}
                                        onChange={(e) => setFuelPaymentMethod(e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="fuelCash">
                                        Cash
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input 
                                        className="form-check-input" 
                                        type="radio" 
                                        name="fuelPaymentMethod" 
                                        id="fuelCard" 
                                        value="card"
                                        checked={fuelPaymentMethod === 'card'}
                                        onChange={(e) => setFuelPaymentMethod(e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="fuelCard">
                                        Card
                                    </label>
                                </div>
                            </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-light" onClick={() => setShowFuelPayModal(false)}>Cancel</button>
                        <button type="button" className="btn btn-base" onClick={submitFuelPayment}>Confirm Paid</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
